import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);
  const [expandedCard, setExpandedCard] = useState(0);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const testimonials = [
    {
      rating: 5.0,
      text: "SkyFamilyTrip provided my mother not only with safety, but above all with dignity during her flight to New York. The assistant was discreet, professional, and extremely caring. It was the best decision.",
      name: "Anna Kowalska",
      role: "Warsaw, Poland",
      avatar: "https://public.readdy.ai/ai/img_res/e42788bac0ef051758385914f51e3168.jpg"
    },
    {
      rating: 5.0,
      text: "As an entrepreneur, I frequently travel between Europe and the USA. SkyFamilyTrip ensures my son receives safe care during flights. The communication is excellent, and I have complete peace of mind.",
      name: "Michał Nowak",
      role: "Krakow, Poland",
      avatar: "https://readdy.ai/api/search-image?query=successful%20polish%20businessman%20in%20his%2040s%20confident%20expression%20wearing%20elegant%20suit%20against%20soft%20background%20professional%20portrait&width=160&height=160&seq=testimonial2&orientation=squarish"
    },
    {
      rating: 5.0,
      text: "Our dog traveled with a SkyFamilyTrip assistant on board a private jet. Professionalism, care, and complete discretion. This is the standard we were looking for.",
      name: "Katarzyna Wiśniewska",
      role: "Monaco",
      avatar: "https://readdy.ai/api/search-image?query=elegant%20sophisticated%20woman%20in%20her%2050s%20with%20refined%20style%20wearing%20luxury%20clothing%20against%20soft%20neutral%20background%20professional%20portrait&width=160&height=160&seq=testimonial3&orientation=squarish"
    }
  ];

  const services = [
    {
      icon: "👴",
      title: "Senior Personal Flight Assistant",
      description: "SkyFamilyTrip is your personal assistant during the flight — providing dignity, comfort, and full support at every stage of the journey for seniors on commercial flights and private jets.",
      image: "https://readdy.ai/api/search-image?query=professional%20elegant%20flight%20attendant%20assisting%20senior%20passenger%20in%20luxury%20private%20jet%20cabin%20warm%20lighting%20sophisticated%20interior%20premium%20aviation%20service%20dignified%20atmosphere&width=600&height=800&seq=service1&orientation=portrait"
    },
    {
      icon: "👶",
      title: "Private Child Flight Assistant",
      description: "SkyFamilyTrip is your personal assistant during the flight — exclusive care for children traveling internationally with full door-to-door assistance and discreet communication with parents.",
      image: "https://readdy.ai/api/search-image?query=professional%20caring%20female%20flight%20nanny%20in%20elegant%20uniform%20holding%20hands%20with%20nine%20year%20old%20european%20white%20boy%20in%20luxury%20airport%20VIP%20lounge%20bright%20clean%20modern%20interior%20safe%20comfortable%20environment%20premium%20childcare%20aviation%20service%20warm%20caring%20interaction%20reassuring%20presence&width=600&height=800&seq=service2new&orientation=portrait"
    },
    {
      icon: "🐶",
      title: "Private Pet Flight Assistant",
      description: "SkyFamilyTrip is your personal assistant during the flight — luxury pet transport in cabin or private jet. Your pet deserves the highest standard of care during travel.",
      image: "https://readdy.ai/api/search-image?query=elegant%20small%20dog%20with%20professional%20pet%20handler%20in%20luxury%20private%20jet%20cabin%20comfortable%20premium%20pet%20travel%20sophisticated%20interior%20warm%20lighting%20high-end%20aviation%20service&width=600&height=800&seq=service3&orientation=portrait"
    }
  ];

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-lg' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-8 py-6 flex items-center justify-between">
          <div className="flex items-center">
            <img 
              src="https://static.readdy.ai/image/d4eacc92f98f8a6620fd285058e7b7e6/c1188670ce78974d01eed9b0f48908c9.jpeg" 
              alt="SkyFamilyTrip Logo" 
              className="h-12 w-auto"
            />
          </div>
          <div className="flex items-center gap-10">
            <a href="#home" className={`text-sm font-medium transition-colors hover:text-[#d4af37] ${scrolled ? 'text-[#1a2332]' : 'text-white'}`}>Home</a>
            <a href="#services" className={`text-sm font-medium transition-colors hover:text-[#d4af37] ${scrolled ? 'text-[#1a2332]' : 'text-white'}`}>Services</a>
            <a href="#senior" className={`text-sm font-medium transition-colors hover:text-[#d4af37] ${scrolled ? 'text-[#1a2332]' : 'text-white'}`}>Senior</a>
            <a href="#child" className={`text-sm font-medium transition-colors hover:text-[#d4af37] ${scrolled ? 'text-[#1a2332]' : 'text-white'}`}>Children</a>
            <a href="#pet" className={`text-sm font-medium transition-colors hover:text-[#d4af37] ${scrolled ? 'text-[#1a2332]' : 'text-white'}`}>Pets</a>
            <a href="#about" className={`text-sm font-medium transition-colors hover:text-[#d4af37] ${scrolled ? 'text-[#1a2332]' : 'text-white'}`}>About</a>
            <a href="#contact" className="px-6 py-2.5 bg-[#1a2332] text-white text-sm font-medium rounded-full hover:bg-[#d4af37] transition-colors whitespace-nowrap cursor-pointer">
              Contact
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center">
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        <img 
          src="https://readdy.ai/api/search-image?query=ultra%20luxury%20private%20jet%20interior%20with%20elegant%20cream%20leather%20seats%20sophisticated%20cabin%20design%20warm%20ambient%20lighting%20premium%20aviation%20expensive%20materials%20modern%20sleek%20design%20high-end%20business%20jet%20empty%20cabin%20professional%20photography&width=1920&height=1080&seq=hero1&orientation=landscape" 
          alt="Luxury Private Jet Interior" 
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="relative z-10 max-w-7xl mx-auto px-8 py-32 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center w-full">
          <div className="text-white">
            <h1 className="text-6xl font-bold mb-6 leading-tight">Your Personal Assistant<br/>During the Flight</h1>
            <p className="text-2xl mb-4 leading-relaxed opacity-95 font-medium">SkyFamilyTrip is your personal assistant during the flight</p>
            <p className="text-lg mb-10 leading-relaxed opacity-90">Luxury care during air travel — commercial and private jets. We provide exclusive, discreet, and professional assistance throughout your entire journey.</p>
            <div className="flex gap-4">
              <a href="#contact" className="px-8 py-4 bg-[#1a2332] text-white font-medium rounded-full hover:bg-[#d4af37] transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer">
                Book Now
                <i className="ri-arrow-right-line"></i>
              </a>
              <a href="#services" className="px-8 py-4 bg-transparent border-2 border-white text-white font-medium rounded-full hover:bg-white hover:text-[#1a2332] transition-all whitespace-nowrap cursor-pointer">
                Our Services
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-32 bg-[#faf9f7]">
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=professional%20elegant%20flight%20assistant%20helping%20senior%20passenger%20in%20luxury%20airport%20VIP%20lounge%20warm%20caring%20interaction%20sophisticated%20interior%20premium%20service%20dignified%20atmosphere%20soft%20lighting%20high-end%20aviation&width=800&height=900&seq=mission1&orientation=portrait" 
                alt="Professional Flight Assistant" 
                className="w-full h-full object-cover rounded-[35px] shadow-[0_15px_50px_rgba(26,35,50,0.12)]"
              />
            </div>
            <div>
              <div className="inline-block px-5 py-2 bg-[#d4af37] text-white text-xs font-bold uppercase tracking-wider rounded-full mb-6">
                Our Mission
              </div>
              <h2 className="text-5xl font-serif font-bold text-[#1a2332] mb-6 leading-tight">SkyFamilyTrip is your personal assistant during the flight.</h2>
              <p className="text-lg text-[#5a6c7d] leading-relaxed mb-8">
                Travel is not just a flight — it's an experience that should be peaceful, dignified, and fully cared for. We are your dedicated personal assistant during the flight, creating a new standard of travel for families, VIP clients, and individuals requiring the highest level of safety and comfort.
              </p>
              <p className="text-lg text-[#5a6c7d] leading-relaxed mb-10">
                We provide peace of mind, security, and the presence of your personal assistant — in a space that can often be stressful and demanding.
              </p>
              <a href="#about" className="inline-flex items-center gap-2 px-8 py-4 bg-[#1a2332] text-white font-medium rounded-full hover:bg-[#d4af37] transition-all whitespace-nowrap cursor-pointer">
                Learn More
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section id="services" className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-8">
          <h2 className="text-5xl font-bold text-center text-[#1a2332] mb-20">Our Services ✈️</h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <div
                key={index}
                className={`relative rounded-[24px] overflow-hidden cursor-pointer transition-all duration-600 ${
                  expandedCard === index ? 'lg:col-span-2' : 'lg:col-span-1'
                }`}
                style={{ aspectRatio: '3/4' }}
                onMouseEnter={() => setExpandedCard(index)}
              >
                <img 
                  src={service.image} 
                  alt={service.title} 
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1a2332]/80 via-[#1a2332]/40 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <div className="text-4xl mb-4">{service.icon}</div>
                  <h3 className="text-2xl font-bold text-white mb-4">{service.title}</h3>
                  <p className={`text-white/90 text-base leading-relaxed transition-all duration-300 ${
                    expandedCard === index ? 'opacity-100 max-h-40' : 'opacity-0 max-h-0 overflow-hidden'
                  }`}>
                    {service.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-8">
          <div className="text-center mb-20">
            <div className="inline-block px-5 py-2 bg-[#d4af37] text-white text-xs font-bold uppercase tracking-wider rounded-full mb-6">
              Simple Process
            </div>
            <h2 className="text-5xl font-bold text-[#1a2332] mb-6">How It Works</h2>
            <p className="text-xl text-[#5a6c7d] max-w-3xl mx-auto leading-relaxed">
              Booking your personal flight assistant is seamless and personalized to your needs
            </p>
          </div>
          
          <div className="relative">
            {/* Connection Line */}
            <div className="hidden lg:block absolute top-24 left-[12.5%] right-[12.5%] h-0.5 bg-gradient-to-r from-[#d4af37]/20 via-[#d4af37] to-[#d4af37]/20"></div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {/* Step 1 */}
              <div className="relative group">
                <div className="bg-[#faf9f7] p-8 rounded-[24px] text-center transition-all duration-300 hover:shadow-[0_15px_50px_rgba(212,175,55,0.15)] hover:-translate-y-2">
                  <div className="relative mx-auto w-20 h-20 bg-gradient-to-br from-[#d4af37] to-[#c9a961] rounded-full flex items-center justify-center mb-8 shadow-lg">
                    <span className="text-white text-3xl font-bold">1</span>
                  </div>
                  <div className="w-14 h-14 mx-auto bg-white rounded-full flex items-center justify-center mb-6 shadow-md">
                    <i className="ri-phone-line text-[#d4af37] text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-[#1a2332] mb-4">Initial Consultation</h3>
                  <p className="text-[#5a6c7d] leading-relaxed text-sm">
                    Contact us via phone, email, or form. Share your travel details and specific requirements for your personal flight assistant.
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="relative group">
                <div className="bg-[#faf9f7] p-8 rounded-[24px] text-center transition-all duration-300 hover:shadow-[0_15px_50px_rgba(212,175,55,0.15)] hover:-translate-y-2 lg:mt-12">
                  <div className="relative mx-auto w-20 h-20 bg-gradient-to-br from-[#d4af37] to-[#c9a961] rounded-full flex items-center justify-center mb-8 shadow-lg">
                    <span className="text-white text-3xl font-bold">2</span>
                  </div>
                  <div className="w-14 h-14 mx-auto bg-white rounded-full flex items-center justify-center mb-6 shadow-md">
                    <i className="ri-user-search-line text-[#d4af37] text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-[#1a2332] mb-4">Assistant Matching</h3>
                  <p className="text-[#5a6c7d] leading-relaxed text-sm">
                    We carefully select your perfect personal flight assistant based on your needs, language preferences, and travel requirements.
                  </p>
                </div>
              </div>

              {/* Step 3 */}
              <div className="relative group">
                <div className="bg-[#faf9f7] p-8 rounded-[24px] text-center transition-all duration-300 hover:shadow-[0_15px_50px_rgba(212,175,55,0.15)] hover:-translate-y-2">
                  <div className="relative mx-auto w-20 h-20 bg-gradient-to-br from-[#d4af37] to-[#c9a961] rounded-full flex items-center justify-center mb-8 shadow-lg">
                    <span className="text-white text-3xl font-bold">3</span>
                  </div>
                  <div className="w-14 h-14 mx-auto bg-white rounded-full flex items-center justify-center mb-6 shadow-md">
                    <i className="ri-file-list-3-line text-[#d4af37] text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-[#1a2332] mb-4">Confirmation & Planning</h3>
                  <p className="text-[#5a6c7d] leading-relaxed text-sm">
                    Receive a detailed itinerary and service agreement. We coordinate all logistics including VIP services and special requests.
                  </p>
                </div>
              </div>

              {/* Step 4 */}
              <div className="relative group">
                <div className="bg-[#faf9f7] p-8 rounded-[24px] text-center transition-all duration-300 hover:shadow-[0_10px_50px_rgba(26,35,50,0.12)]">
                  <div className="relative mx-auto w-20 h-20 bg-gradient-to-br from-[#d4af37] to-[#c9a961] rounded-full flex items-center justify-center mb-8 shadow-lg">
                    <span className="text-white text-3xl font-bold">4</span>
                  </div>
                  <div className="w-14 h-14 mx-auto bg-white rounded-full flex items-center justify-center mb-6 shadow-md">
                    <i className="ri-plane-line text-[#d4af37] text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-[#1a2332] mb-4">Travel Day</h3>
                  <p className="text-[#5a6c7d] leading-relaxed text-sm">
                    Your personal assistant meets you at the designated location and provides full support throughout the entire journey until safe arrival.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-16 text-center">
            <a href="#contact" className="inline-flex items-center gap-2 px-10 py-4 bg-[#1a2332] text-white font-medium rounded-full hover:bg-[#d4af37] transition-all whitespace-nowrap cursor-pointer">
              Start Your Booking
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>
      </section>

      {/* Senior Flight Companion */}
      <section id="senior" className="py-32 bg-[#faf9f7]">
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="text-5xl mb-6">🧓</div>
              <h2 className="text-4xl font-bold text-[#1a2332] mb-6">Senior Personal Flight Assistant</h2>
              <p className="text-xl text-[#5a6c7d] mb-8 leading-relaxed">SkyFamilyTrip is your personal assistant during the flight — providing dignity, comfort, and full support at every stage of the journey</p>
              <div className="space-y-4 mb-10">
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-[#d4af37] text-xl mt-1 flex-shrink-0 w-6 h-6 flex items-center justify-center"></i>
                  <p className="text-[#5a6c7d]">Personal assistance during commercial flights and private jets</p>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-[#d4af37] text-xl mt-1 flex-shrink-0 w-6 h-6 flex items-center justify-center"></i>
                  <p className="text-[#5a6c7d]">VIP check-in process / Fast Track</p>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-[#d4af37] text-xl mt-1 flex-shrink-0 w-6 h-6 flex items-center justify-center"></i>
                  <p className="text-[#5a6c7d]">Lounge organization and concierge services</p>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-[#d4af37] text-xl mt-1 flex-shrink-0 w-6 h-6 flex items-center justify-center"></i>
                  <p className="text-[#5a6c7d]">Support during international connections</p>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-[#d4af37] text-xl mt-1 flex-shrink-0 w-6 h-6 flex items-center justify-center"></i>
                  <p className="text-[#5a6c7d]">Discreet medical support (medication reminders, comfort monitoring)</p>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-[#d4af37] text-xl mt-1 flex-shrink-0 w-6 h-6 flex items-center justify-center"></i>
                  <p className="text-[#5a6c7d]">Constant communication with family</p>
                </div>
              </div>
              <p className="text-lg text-[#1a2332] font-medium italic">Your personal assistant cares not only about safety, but also about your sense of dignity and peace.</p>
            </div>
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=caring%20professional%20flight%20companion%20assisting%20elderly%20senior%20passenger%20in%20luxury%20private%20jet%20cabin%20warm%20compassionate%20interaction%20premium%20aviation%20service%20comfortable%20seating%20dignified%20atmosphere%20soft%20lighting&width=800&height=900&seq=senior1&orientation=portrait" 
                alt="Senior Flight Companion" 
                className="w-full h-full object-cover rounded-[35px] shadow-[0_15px_50px_rgba(26,35,50,0.12)]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Child Flight Nanny */}
      <section id="child" className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative order-2 lg:order-1">
              <div className="grid grid-cols-2 gap-4">
                <img 
                  src="https://readdy.ai/api/search-image?query=professional%20caring%20female%20flight%20nanny%20in%20elegant%20uniform%20holding%20hands%20with%20nine%20year%20old%20european%20white%20boy%20walking%20through%20luxury%20airport%20terminal%20bright%20clean%20modern%20interior%20safe%20comfortable%20environment%20premium%20childcare%20service%20warm%20caring%20interaction%20reassuring%20presence&width=400&height=500&seq=child2a&orientation=portrait" 
                  alt="Child Flight Nanny Walking" 
                  className="w-full h-[500px] object-cover object-top rounded-[24px] shadow-[0_15px_50px_rgba(26,35,50,0.12)]"
                />
                <img 
                  src="https://readdy.ai/api/search-image?query=professional%20female%20flight%20nanny%20sitting%20with%20nine%20year%20old%20european%20white%20boy%20in%20luxury%20private%20jet%20cabin%20reading%20book%20together%20comfortable%20premium%20seating%20warm%20lighting%20caring%20interaction%20happy%20child%20safe%20environment%20high-end%20aviation%20service&width=400&height=500&seq=child2b&orientation=portrait" 
                  alt="Child Flight Nanny on Plane" 
                  className="w-full h-[500px] object-cover object-top rounded-[24px] shadow-[0_15px_50px_rgba(26,35,50,0.12)]"
                />
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <div className="text-5xl mb-6">👶</div>
              <h2 className="text-4xl font-bold text-[#1a2332] mb-6">Private Child Flight Assistant</h2>
              <p className="text-xl text-[#5a6c7d] mb-8 leading-relaxed">SkyFamilyTrip is your personal assistant during the flight — exclusive care for children traveling internationally</p>
              <div className="space-y-4 mb-10">
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-[#d4af37] text-xl mt-1 flex-shrink-0 w-6 h-6 flex items-center justify-center"></i>
                  <p className="text-[#5a6c7d]">Door-to-door care (upon request)</p>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-[#d4af37] text-xl mt-1 flex-shrink-0 w-6 h-6 flex items-center justify-center"></i>
                  <p className="text-[#5a6c7d]">Full assistance in VIP zones</p>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-[#d4af37] text-xl mt-1 flex-shrink-0 w-6 h-6 flex items-center justify-center"></i>
                  <p className="text-[#5a6c7d]">Care throughout the entire flight</p>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-[#d4af37] text-xl mt-1 flex-shrink-0 w-6 h-6 flex items-center justify-center"></i>
                  <p className="text-[#5a6c7d]">Safe handover of the child to the designated person</p>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-[#d4af37] text-xl mt-1 flex-shrink-0 w-6 h-6 flex items-center justify-center"></i>
                  <p className="text-[#5a6c7d]">Constant, private communication with parents</p>
                </div>
              </div>
              <p className="text-lg text-[#1a2332] font-medium italic">This is a solution for international families, entrepreneurs, and high-net-worth clients who need a personal assistant during the flight.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pet Flight Companion */}
      <section id="pet" className="py-32 bg-[#faf9f7]">
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="text-5xl mb-6">🐶</div>
              <h2 className="text-4xl font-bold text-[#1a2332] mb-6">Private Pet Flight Assistant</h2>
              <p className="text-xl text-[#5a6c7d] mb-8 leading-relaxed">SkyFamilyTrip is your personal assistant during the flight — luxury pet transport in cabin or private jet</p>
              <p className="text-lg text-[#5a6c7d] mb-8 leading-relaxed">Your pet deserves the highest standard of care with a dedicated personal assistant.</p>
              <div className="space-y-4 mb-10">
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-[#d4af37] text-xl mt-1 flex-shrink-0 w-6 h-6 flex items-center justify-center"></i>
                  <p className="text-[#5a6c7d]">Personal accompaniment of pets in commercial flight cabins</p>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-[#d4af37] text-xl mt-1 flex-shrink-0 w-6 h-6 flex items-center justify-center"></i>
                  <p className="text-[#5a6c7d]">Travel on board private jets</p>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-[#d4af37] text-xl mt-1 flex-shrink-0 w-6 h-6 flex items-center justify-center"></i>
                  <p className="text-[#5a6c7d]">Pick-up and safe handover to the owner</p>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-[#d4af37] text-xl mt-1 flex-shrink-0 w-6 h-6 flex items-center justify-center"></i>
                  <p className="text-[#5a6c7d]">Support with international documentation</p>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-check-line text-[#d4af37] text-xl mt-1 flex-shrink-0 w-6 h-6 flex items-center justify-center"></i>
                  <p className="text-[#5a6c7d]">Calm, human-centered care throughout the entire journey</p>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=elegant%20small%20dog%20with%20professional%20pet%20handler%20in%20luxury%20private%20jet%20cabin%20comfortable%20premium%20pet%20travel%20sophisticated%20interior%20warm%20lighting%20high-end%20aviation%20service&width=800&height=900&seq=pet1&orientation=portrait" 
                alt="Pet Flight Companion" 
                className="w-full h-full object-cover rounded-[35px] shadow-[0_10px_50px_rgba(26,35,50,0.12)]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* About / Standards Section */}
      <section id="about" className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-bold text-[#1a2332] mb-6">The SkyFamilyTrip Standard</h2>
            <p className="text-xl text-[#5a6c7d] max-w-3xl mx-auto leading-relaxed">
              Your personal assistant during the flight — for clients who accept no compromises
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
            <div className="bg-[#faf9f7] p-8 rounded-[24px]">
              <div className="w-14 h-14 bg-[#d4af37] rounded-full flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-[#1a2332] mb-4">Verified Assistants</h3>
              <p className="text-[#5a6c7d] leading-relaxed">Carefully selected, trained professionals with experience in commercial flights and private aviation as your personal assistant.</p>
            </div>
            <div className="bg-[#faf9f7] p-8 rounded-[24px]">
              <div className="w-14 h-14 bg-[#d4af37] rounded-full flex items-center justify-center mb-6">
                <i className="ri-lock-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-[#1a2332] mb-4">Complete Discretion</h3>
              <p className="text-[#5a6c7d] leading-relaxed">Confidentiality and discretion at the highest level. Your privacy is our priority.</p>
            </div>
            <div className="bg-[#faf9f7] p-8 rounded-[24px]">
              <div className="w-14 h-14 bg-[#d4af37] rounded-full flex items-center justify-center mb-6">
                <i className="ri-global-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-[#1a2332] mb-4">Global Service</h3>
              <p className="text-[#5a6c7d] leading-relaxed">We operate worldwide, ensuring a consistent standard of service regardless of location.</p>
            </div>
            <div className="bg-[#faf9f7] p-8 rounded-[24px]">
              <div className="w-14 h-14 bg-[#d4af37] rounded-full flex items-center justify-center mb-6">
                <i className="ri-plane-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-[#1a2332] mb-4">Private Aviation</h3>
              <p className="text-[#5a6c7d] leading-relaxed">Experience in commercial flights and private jets at the highest level.</p>
            </div>
            <div className="bg-[#faf9f7] p-8 rounded-[24px]">
              <div className="w-14 h-14 bg-[#d4af37] rounded-full flex items-center justify-center mb-6">
                <i className="ri-user-star-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-[#1a2332] mb-4">Individual Concierge</h3>
              <p className="text-[#5a6c7d] leading-relaxed">A dedicated personal assistant adapting every aspect of travel to your needs.</p>
            </div>
            <div className="bg-[#faf9f7] p-8 rounded-[24px]">
              <div className="w-14 h-14 bg-[#d4af37] rounded-full flex items-center justify-center mb-6">
                <i className="ri-star-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-[#1a2332] mb-4">Highest Standards</h3>
              <p className="text-[#5a6c7d] leading-relaxed">Transparency, safety, and premium quality in every detail.</p>
            </div>
          </div>
          <div className="text-center">
            <p className="text-2xl text-[#1a2332] font-medium italic mb-8">
              SkyFamilyTrip is your personal assistant during the flight — the choice of international families, investors, entrepreneurs, and individuals who expect more than standard service.
            </p>
            <p className="text-xl text-[#5a6c7d] leading-relaxed max-w-4xl mx-auto">
              We provide peace of mind, security, and the presence of your personal assistant — in a space that can often be stressful and demanding.
            </p>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-32 bg-[#faf9f7]">
        <div className="max-w-4xl mx-auto px-8">
          <h2 className="text-4xl font-serif text-center text-[#1a2332] mb-16">(What Our Clients Say)</h2>
          <div className="relative">
            <div className="mb-8">
              <div className="inline-flex items-center gap-2 px-5 py-2 bg-[#1a2332] rounded-[20px] mb-6">
                <i className="ri-star-fill text-[#d4af37] text-lg"></i>
                <span className="text-white font-bold">{testimonials[currentTestimonial].rating}</span>
              </div>
              <p className="text-2xl font-serif text-[#2a3a4a] leading-relaxed mb-8">
                "{testimonials[currentTestimonial].text}"
              </p>
              <div className="flex items-center gap-4">
                <img 
                  src={testimonials[currentTestimonial].avatar} 
                  alt={testimonials[currentTestimonial].name} 
                  className="w-20 h-20 rounded-full object-cover"
                />
                <div>
                  <p className="text-xl font-bold text-[#1a2332]">{testimonials[currentTestimonial].name}</p>
                  <p className="text-base text-[#7a8a9a]">{testimonials[currentTestimonial].role}</p>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button 
                onClick={() => setCurrentTestimonial((prev) => (prev === 0 ? testimonials.length - 1 : prev - 1))}
                className="w-12 h-12 bg-white border-2 border-[#1a2332] rounded-xl flex items-center justify-center hover:bg-[#1a2332] hover:text-white transition-all cursor-pointer"
              >
                <i className="ri-arrow-left-line text-xl"></i>
              </button>
              <button 
                onClick={() => setCurrentTestimonial((prev) => (prev === testimonials.length - 1 ? 0 : prev + 1))}
                className="w-12 h-12 bg-[#1a2332] text-white rounded-xl flex items-center justify-center hover:bg-[#d4af37] transition-all cursor-pointer"
              >
                <i className="ri-arrow-right-line text-xl"></i>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 bg-white">
        <div className="max-w-5xl mx-auto px-8 text-center">
          <h2 className="text-5xl font-bold text-[#1a2332] mb-6 leading-tight">Your Personal Assistant During the Flight</h2>
          <p className="text-xl text-[#5a6c7d] leading-relaxed mb-10 max-w-3xl mx-auto">
            Join the circle of clients who have chosen SkyFamilyTrip as their personal assistant during the flight. Contact us and discover the possibilities.
          </p>
          <a href="#contact" className="inline-flex items-center gap-2 px-12 py-5 bg-[#1a2332] text-white text-lg font-medium rounded-full hover:bg-[#d4af37] transition-all whitespace-nowrap cursor-pointer">
            Contact Us
            <i className="ri-arrow-right-line"></i>
          </a>
          <div className="mt-20 flex justify-center items-center gap-8 flex-wrap">
            <img 
              src="https://readdy.ai/api/search-image?query=luxury%20private%20jet%20interior%20elegant%20cabin%20design%20premium%20aviation%20sophisticated%20seating%20warm%20lighting%20high-end%20business%20jet%20empty%20professional&width=300&height=200&seq=cta1&orientation=landscape" 
              alt="Luxury Jet" 
              className="w-64 h-40 object-cover rounded-[24px] shadow-lg"
            />
            <img 
              src="https://readdy.ai/api/search-image?query=professional%20flight%20assistant%20in%20elegant%20uniform%20standing%20in%20luxury%20airport%20VIP%20lounge%20sophisticated%20interior%20premium%20service%20confident%20posture&width=300&height=200&seq=cta2&orientation=landscape" 
              alt="Professional Assistant" 
              className="w-64 h-40 object-cover rounded-[24px] shadow-lg"
            />
            <img 
              src="https://readdy.ai/api/search-image?query=happy%20senior%20passenger%20smiling%20in%20comfortable%20luxury%20airport%20lounge%20premium%20travel%20experience%20dignified%20atmosphere%20warm%20lighting%20sophisticated%20setting&width=300&height=200&seq=cta3&orientation=landscape" 
              alt="Happy Senior" 
              className="w-64 h-40 object-cover rounded-[24px] shadow-lg"
            />
            <img 
              src="https://readdy.ai/api/search-image?query=child%20with%20caring%20nanny%20in%20bright%20modern%20VIP%20lounge%20safe%20comfortable%20environment%20premium%20childcare%20service%20warm%20interaction%20happy%20atmosphere&width=300&height=200&seq=cta4&orientation=landscape" 
              alt="Child with Nanny" 
              className="w-64 h-40 object-cover rounded-[24px] shadow-lg"
            />
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact" className="py-32 bg-[#faf9f7]">
        <div className="max-w-4xl mx-auto px-8">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-[#1a2332] mb-6">Contact Us</h2>
            <p className="text-xl text-[#5a6c7d] leading-relaxed">
              Fill out the form, and our team will contact you within 24 hours.
            </p>
          </div>
          <form 
            id="contact_form"
            data-readdy-form
            action="https://readdy.ai/api/form/d66ahb4kan35qnu5ik0g" 
            method="POST"
            className="bg-white p-10 rounded-[30px] shadow-[0_15px_50px_rgba(26,35,50,0.08)]"
            onSubmit={(e) => {
              e.preventDefault();
              const form = e.currentTarget;
              const formData = new FormData(form);
              
              // Validation
              const name = formData.get('name') as string;
              const email = formData.get('email') as string;
              const phone = formData.get('phone') as string;
              const message = formData.get('message') as string;
              
              if (!name || !email || !phone || !message) {
                alert('Please fill in all fields');
                return;
              }
              
              if (message.length > 500) {
                alert('Message cannot exceed 500 characters');
                return;
              }
              
              // Submit
              fetch('https://readdy.ai/api/form/d66ahb4kan35qnu5ik0g', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: new URLSearchParams(formData as any).toString()
              })
              .then(response => {
                if (response.ok) {
                  alert('Thank you for your message! We will contact you soon.');
                  form.reset();
                } else {
                  alert('An error occurred. Please try again.');
                }
              })
              .catch(() => {
                alert('An error occurred. Please try again.');
              });
            }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-[#1a2332] mb-2">Full Name *</label>
                <input 
                  type="text" 
                  id="name"
                  name="name"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d4af37] text-sm"
                  placeholder="John Smith"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-[#1a2332] mb-2">Email *</label>
                <input 
                  type="email" 
                  id="email"
                  name="email"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d4af37] text-sm"
                  placeholder="john@example.com"
                />
              </div>
            </div>
            <div className="mb-6">
              <label htmlFor="phone" className="block text-sm font-medium text-[#1a2332] mb-2">Phone *</label>
              <input 
                type="tel" 
                id="phone"
                name="phone"
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d4af37] text-sm"
                placeholder="+1 234 567 890"
              />
            </div>
            <div className="mb-6">
              <label htmlFor="service" className="block text-sm font-medium text-[#1a2332] mb-2">Service of Interest</label>
              <select 
                id="service"
                name="service"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d4af37] text-sm cursor-pointer"
              >
                <option value="">Select a service</option>
                <option value="senior">Senior Private Flight Companion</option>
                <option value="child">Private Child Flight Nanny</option>
                <option value="pet">Private Pet Flight Companion</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="mb-8">
              <label htmlFor="message" className="block text-sm font-medium text-[#1a2332] mb-2">Message * (max 500 characters)</label>
              <textarea 
                id="message"
                name="message"
                required
                maxLength={500}
                rows={5}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d4af37] resize-none text-sm"
                placeholder="Describe your needs..."
              ></textarea>
            </div>
            <button 
              type="submit"
              className="w-full px-8 py-4 bg-[#1a2332] text-white font-medium rounded-full hover:bg-[#d4af37] transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
            >
              Send Message
              <i className="ri-send-plane-line"></i>
            </button>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white py-10 px-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-[#1a2332] rounded-[30px] px-16 py-20 relative overflow-hidden">
            <div className="absolute bottom-0 right-0 text-[120px] font-bold text-white/5 leading-none whitespace-nowrap">
              SKYFAMILYTRIP
            </div>
            <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
              <div>
                <h3 className="text-3xl font-bold text-white mb-4">Book Your Journey</h3>
              </div>
              <div>
                <h4 className="text-lg font-bold text-white uppercase tracking-wider mb-6">Services</h4>
                <ul className="space-y-4">
                  <li><a href="#senior" className="flex items-center gap-3 text-[#b0bac5] hover:text-[#d4af37] transition-colors cursor-pointer">
                      <i className="ri-phone-line text-2xl w-6 h-6 flex items-center justify-center"></i>
                      Senior Companion
                    </a></li>
                  <li><a href="#child" className="flex items-center gap-3 text-[#b0bac5] hover:text-[#d4af37] transition-colors cursor-pointer">
                      <i className="ri-user-search-line text-2xl w-6 h-6 flex items-center justify-center"></i>
                      Child Nanny
                    </a></li>
                  <li><a href="#pet" className="flex items-center gap-3 text-[#b0bac5] hover:text-[#d4af37] transition-colors cursor-pointer">
                      <i className="ri-plane-line text-2xl w-6 h-6 flex items-center justify-center"></i>
                      Pet Companion
                    </a></li>
                  <li><a href="#about" className="flex items-center gap-3 text-[#b0bac5] hover:text-[#d4af37] transition-colors cursor-pointer">
                      <i className="ri-shield-check-line text-2xl w-6 h-6 flex items-center justify-center"></i>
                      About Us
                    </a></li>
                  <li><a href="#contact" className="flex items-center gap-3 text-[#b0bac5] hover:text-[#d4af37] transition-colors cursor-pointer">
                      <i className="ri-phone-line text-2xl w-6 h-6 flex items-center justify-center"></i>
                      Contact
                    </a></li>
                </ul>
              </div>
              <div>
                <h4 className="text-lg font-bold text-white uppercase tracking-wider mb-6">Follow Us</h4>
                <ul className="space-y-4">
                  <li>
                    <a href="#" className="flex items-center gap-3 text-[#b0bac5] hover:text-[#d4af37] transition-colors cursor-pointer">
                      <i className="ri-instagram-line text-2xl w-6 h-6 flex items-center justify-center"></i>
                      Instagram
                    </a>
                  </li>
                  <li>
                    <a href="#" className="flex items-center gap-3 text-[#b0bac5] hover:text-[#d4af37] transition-colors cursor-pointer">
                      <i className="ri-linkedin-line text-2xl w-6 h-6 flex items-center justify-center"></i>
                      LinkedIn
                    </a>
                  </li>
                  <li>
                    <a href="#" className="flex items-center gap-3 text-[#b0bac5] hover:text-[#d4af37] transition-colors cursor-pointer">
                      <i className="ri-facebook-line text-2xl w-6 h-6 flex items-center justify-center"></i>
                      Facebook
                    </a>
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="text-lg font-bold text-white uppercase tracking-wider mb-6">Newsletter</h4>
                <p className="text-[#b0bac5] text-sm mb-6">Stay updated with our services</p>
                <form 
                  id="newsletter_form"
                  data-readdy-form
                  action="https://readdy.ai/api/form/d66ahb4kan35qnu5ik0g"
                  method="POST"
                  className="flex"
                  onSubmit={(e) => {
                    e.preventDefault();
                    const form = e.currentTarget;
                    const formData = new FormData(form);
                    const email = formData.get('email') as string;
                    
                    if (!email) {
                      alert('Please enter your email address');
                      return;
                    }
                    
                    fetch('https://readdy.ai/api/form/d66ahb4kan35qnu5ik0g', {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                      },
                      body: new URLSearchParams(formData as any).toString()
                    })
                    .then(response => {
                      if (response.ok) {
                        alert('Thank you for subscribing!');
                        form.reset();
                      } else {
                        alert('An error occurred. Please try again.');
                      }
                    })
                    .catch(() => {
                      alert('An error occurred. Please try again.');
                    });
                  }}
                >
                  <input 
                    type="email" 
                    name="email"
                    required
                    placeholder="Your email" 
                    className="flex-1 px-4 py-3 bg-[#2a3a4a] border border-[#3a4a5a] text-white placeholder-[#7a8a9a] rounded-l-full focus:outline-none focus:ring-2 focus:ring-[#d4af37] text-sm"
                  />
                  <button 
                    type="submit"
                    className="w-14 h-14 bg-white rounded-full flex items-center justify-center hover:bg-[#d4af37] transition-all cursor-pointer"
                  >
                    <i className="ri-arrow-right-line text-[#1a2332] text-xl"></i>
                  </button>
                </form>
              </div>
            </div>
          </div>
          <div className="mt-8 text-center">
            <p className="text-sm text-[#7a8a9a]">
              © 2025 SkyFamilyTrip. Private Presence. Global Confidence. | 
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="ml-2 hover:text-[#d4af37] transition-colors">Powered by Readdy</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
